import { Link } from 'react-router-dom'
import { PRIMARY, PRICING, fmtMoney, fmtPct, pnlClass } from '../data/demo'
import { AllocationBars, EarlyAccessForm, EquityChart, Metric, useModal } from '../components/ui'

export function HomePage() {
  const modal = useModal()
  const a = PRIMARY
  const last = a.equityCurve.slice(-60)

  return (
    <>
      <section className="hero">
        <div className="container hero-grid">
          <div>
            <div className="kicker">
              <span className="pulse" aria-hidden />
              Grok experiment · verified performance OS
            </div>
            <h1>
              Your P&amp;L is a claim.
              <br />
              <span className="lime">Atlas turns it into proof.</span>
            </h1>
            <p className="hero-lead">
              Most trackers bury weak performance in chart noise. Atlas exposes the truth fast — verified track
              records, risk that cannot hide behind averages, and a journal that scores behavior, not vibes.
            </p>
            <div className="hero-actions">
              <Link to="/dashboard" className="btn btn-primary">
                Open demo command center
              </Link>
              <Link to="/proof/grok-alpha" className="btn btn-secondary">
                View public proof
              </Link>
            </div>
            <div className="hero-proof-row">
              <span className="badge verified">Read-only verification</span>
              <span>No investor passwords collected</span>
              <span>Deposits stripped from performance</span>
            </div>
          </div>

          <aside className="card command-center" aria-label="Live-looking performance command center">
            <div className="cc-top">
              <div>
                <div className="badge demo">DEMO · fictional data</div>
                <div style={{ marginTop: 8, fontFamily: 'var(--font-display)', fontSize: '1.25rem' }}>
                  {a.name}
                </div>
                <div className="mute mono" style={{ fontSize: '0.8rem' }}>
                  Fresh {a.freshnessMinutes}m · {a.trackRecordDays}d verified
                </div>
              </div>
              <div className="badge verified">
                <span className="pulse" aria-hidden /> Verified
              </div>
            </div>
            <div className="cc-chart">
              <EquityChart data={last} height={180} />
            </div>
            <div className="cc-metrics">
              <div className="panel">
                <Metric label="Net return" value={fmtPct(a.netReturnPct)} tone="lime" sub="after D/W" />
              </div>
              <div className="panel">
                <Metric label="Max DD" value={fmtPct(-a.maxDrawdownPct)} tone="danger" sub="peak to trough" />
              </div>
              <div className="panel">
                <Metric label="Sharpe" value={a.sharpe.toFixed(2)} sub={`Sortino ${a.sortino.toFixed(2)}`} />
              </div>
              <div className="panel">
                <Metric label="Profit factor" value={a.profitFactor.toFixed(2)} />
              </div>
              <div className="panel">
                <Metric label="Win rate" value={`${a.winRate}%`} />
              </div>
              <div className="panel">
                <Metric label="Open heat" value={`${Math.round(a.openRisk.heat * 100)}%`} tone="amber" />
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="section-tight" id="positioning">
        <div className="container">
          <div className="card card-pad split-2" style={{ alignItems: 'center' }}>
            <div>
              <div className="kicker">Proof, not screenshots</div>
              <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.6rem)', margin: '0 0 0.75rem' }}>
                Performance without context is meaningless.
              </h2>
              <p className="muted" style={{ margin: 0 }}>
                Anyone can crop a green month. Atlas seals a read-only feed, reconciles deposits and withdrawals,
                publishes methodology, and keeps a verification pulse you can audit — equity curve, checksum trail,
                risk boundary.
              </p>
            </div>
            <div className="stack">
              <div className="notice ok">Returns without verification are marketing.</div>
              <div className="notice warn">Risk hidden behind averages is dangerous.</div>
              <div className="notice">A journal without behavioral feedback is just storage.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="products">
        <div className="container">
          <div className="kicker">Four surfaces. One truth model.</div>
          <h2 className="display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', marginTop: 0 }}>
            Tracker. Proof. Risk OS. Journal Lab.
          </h2>
          <div className="split-2" style={{ marginTop: '1.5rem' }}>
            <article className="card feature-card">
              <div className="icon-mark">TR</div>
              <h3>Atlas Track</h3>
              <p>
                Multi-account command center: equity, drawdown, expectancy, session edges, exposure, open risk, and a
                trade table that expands instead of drowning you.
              </p>
              <Link to="/dashboard" className="btn btn-secondary btn-sm">
                Enter tracker
              </Link>
            </article>
            <article className="card feature-card">
              <div className="icon-mark">PF</div>
              <h3>Atlas Proof</h3>
              <p>
                Shareable public track record with verification age, freshness, D/W accounting, and embed card —
                labeled when data is fictional development content.
              </p>
              <Link to="/proof/grok-alpha" className="btn btn-secondary btn-sm">
                Open proof page
              </Link>
            </article>
            <article className="card feature-card">
              <div className="icon-mark amber">RK</div>
              <h3>Risk OS</h3>
              <p>
                Drawdown budgets, loss guards, leverage, concentration, correlation groups, risk-of-ruin estimates,
                and “what breaks first?” stress — no execution.
              </p>
              <Link to="/risk" className="btn btn-secondary btn-sm">
                Open Risk OS
              </Link>
            </article>
            <article className="card feature-card">
              <div className="icon-mark red">JL</div>
              <h3>Journal Lab</h3>
              <p>
                Setup taxonomy, rule adherence, revenge and overtrading signals, session timing, weekly review, and a
                blunt “what changed?” summary.
              </p>
              <Link to="/journal" className="btn btn-secondary btn-sm">
                Open Journal Lab
              </Link>
            </article>
          </div>
        </div>
      </section>

      <section className="section-tight" id="connect">
        <div className="container">
          <div className="kicker">Three connection methods</div>
          <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.5rem)', marginTop: 0 }}>
            Read-only by construction.
          </h2>
          <div className="split-3" style={{ marginTop: '1.25rem' }}>
            <div className="card feature-card">
              <div className="badge verified">Recommended</div>
              <h3>NikaQuant Reporter EA</h3>
              <p>Self-owned read-only data push from your terminal. You hold the keys; Atlas receives digests.</p>
            </div>
            <div className="card feature-card">
              <div className="badge warn">Future</div>
              <h3>Managed connector</h3>
              <p>Broker / MetaApi-style read-only bridge concept. No password forms in this build.</p>
            </div>
            <div className="card feature-card">
              <div className="badge demo">Offline</div>
              <h3>Statement import</h3>
              <p>CSV/HTML reconstruction for historical context — marked unverified until a live feed seals it.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="verification">
        <div className="container split-2">
          <div>
            <div className="kicker">Verification methodology</div>
            <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.5rem)', marginTop: 0 }}>
              Understand verification in under 30 seconds.
            </h2>
            <ol className="muted">
              <li>Establish a read-only heartbeat (EA or future connector).</li>
              <li>Seal baseline balance, equity, and open positions.</li>
              <li>Stream trade and balance events into an append-only digest.</li>
              <li>Reconcile deposits/withdrawals so they never inflate return.</li>
              <li>Publish freshness, age, and methodology beside every public claim.</li>
            </ol>
          </div>
          <div className="card card-pad">
            <h3 className="display" style={{ marginTop: 0 }}>
              Live pulse · {a.name}
            </h3>
            <AllocationBars
              items={a.strategyAllocation.map((s) => ({
                label: s.name,
                pct: s.pct,
                color: s.color,
              }))}
            />
            <p className="mute mono" style={{ marginBottom: 0, marginTop: '1rem', fontSize: '0.8rem' }}>
              checksum demo · sha-theme · {a.verifiedSince} → now · chain intact
            </p>
          </div>
        </div>
      </section>

      <section className="section-tight" id="security">
        <div className="container">
          <div className="card card-pad">
            <div className="kicker">Security boundaries</div>
            <h2 className="display" style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', marginTop: 0 }}>
              What Atlas refuses to touch.
            </h2>
            <div className="split-3">
              <div className="notice">No trading passwords. No investor passwords. Ever.</div>
              <div className="notice">No order placement, modification, or close endpoints.</div>
              <div className="notice danger">No bridge to production app.nikaquant.com from this experiment.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="use-cases">
        <div className="container">
          <div className="kicker">Built for operators</div>
          <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.5rem)', marginTop: 0 }}>
            Who runs Atlas.
          </h2>
          <div className="use-grid" style={{ marginTop: '1.25rem' }}>
            {[
              ['Independent trader', 'One truth stack across personal books — stop screenshot-arguing with yourself.'],
              ['Signal seller', 'Public proof with D/W honesty. Embed cards instead of cropped MT5 history.'],
              ['Prop trader', 'Daily/weekly guards and breach logs that mirror challenge physics.'],
              ['Small desk', 'Portfolio rollup, concentration heat, and team-ready proof skins.'],
            ].map(([t, d]) => (
              <article key={t} className="card feature-card">
                <h3>{t}</h3>
                <p>{d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tight" id="differentiation">
        <div className="container split-2">
          <div>
            <div className="kicker">Differentiation</div>
            <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.4rem)', marginTop: 0 }}>
              Inspired by the useful parts of the category — not a clone.
            </h2>
            <p className="muted">
              Myfxbook-class tracking depth. Institutional risk language. Creator-grade proof packaging. Behavior
              analytics that punish self-deception. Atlas is opinionated: expose weak edges quickly, never decorate
              them.
            </p>
          </div>
          <div className="card card-pad stack">
            <div>
              <strong className="lime">Truth-first UI</strong>
              <p className="muted" style={{ margin: '0.35rem 0 0' }}>
                Drawdown and verification sit above vanity equity zoom.
              </p>
            </div>
            <div>
              <strong className="amber">Risk as a product</strong>
              <p className="muted" style={{ margin: '0.35rem 0 0' }}>
                Budgets and breach physics, not a buried metrics tab.
              </p>
            </div>
            <div>
              <strong>Journal with teeth</strong>
              <p className="muted" style={{ margin: '0.35rem 0 0' }}>
                Rule adherence and revenge tags — storage is not insight.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section" id="testimonials">
        <div className="container">
          <div className="kicker">Concept examples</div>
          <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.4rem)', marginTop: 0 }}>
            Illustrative voices — not real customers.
          </h2>
          <div className="split-3" style={{ marginTop: '1.25rem' }}>
            {[
              [
                '“I stopped sending PNG equity curves. The proof link answers deposit questions before they get asked.”',
                'Signal operator · concept',
              ],
              [
                '“Risk OS showed my correlation cluster before the bad week. Averages had been lying politely.”',
                'Desk lead · concept',
              ],
              [
                '“Journal Lab caught the revenge streak I kept renaming as ‘confidence’.”',
                'Prop challenger · concept',
              ],
            ].map(([q, a]) => (
              <figure key={a} className="card testimonial">
                <div className="badge demo">Concept example</div>
                <blockquote>{q}</blockquote>
                <figcaption className="mute">{a}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tight" id="pricing-preview">
        <div className="container">
          <div className="kicker">Pricing preview</div>
          <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.4rem)', marginTop: 0 }}>
            Simple seats. Concept pricing.
          </h2>
          <p className="notice warn">{PRICING.disclosure}</p>
          <div className="pricing-grid" style={{ marginTop: '1rem' }}>
            {PRICING.plans.map((p) => (
              <article key={p.id} className={`card price-card ${p.featured ? 'featured' : ''}`}>
                {p.featured ? <div className="badge verified">Most chosen concept</div> : null}
                <h3>{p.name}</h3>
                <div className="price">
                  ${p.monthly}
                  <span>/mo</span>
                </div>
                <p className="muted" style={{ margin: 0 }}>
                  {p.blurb}
                </p>
                <ul>
                  {p.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <Link to="/pricing" className={`btn ${p.featured ? 'btn-primary' : 'btn-secondary'} btn-sm`}>
                  View details
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="faq">
        <div className="container" style={{ maxWidth: 800 }}>
          <div className="kicker">FAQ</div>
          <h2 className="display" style={{ fontSize: 'clamp(1.8rem,3vw,2.4rem)', marginTop: 0 }}>
            Straight answers.
          </h2>
          <div className="faq" style={{ marginTop: '1rem' }}>
            {[
              [
                'Is this live production Atlas?',
                'No. This is an isolated Grok development experiment on grok-dev.tracker.nikaquant.com with fictional DEMO data.',
              ],
              [
                'Do you take broker passwords?',
                'No. The recommended path is a self-owned Reporter EA push. Managed connectors are future concepts without password fields here.',
              ],
              [
                'Are the track records real?',
                'No. All accounts, trades, and statistics on this site are fictional development data, clearly labeled DEMO.',
              ],
              [
                'Can Atlas place trades?',
                'No. There is no execution, order modify, or close capability.',
              ],
              [
                'Is billing active?',
                'No. Pricing is a development concept. CTAs open the demo or a local early-access modal.',
              ],
            ].map(([q, ans]) => (
              <details key={q}>
                <summary>{q}</summary>
                <p>{ans}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section-tight">
        <div className="container">
          <div className="card card-pad" style={{ textAlign: 'center' }}>
            <div className="kicker" style={{ justifyContent: 'center' }}>
              Final call
            </div>
            <h2 className="display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 0.75rem' }}>
              Stop decorating the claim. Publish the proof.
            </h2>
            <p className="muted" style={{ maxWidth: 520, margin: '0 auto 1.25rem' }}>
              Equity {fmtMoney(a.equity)} · Net {fmtPct(a.netReturnPct)} · Max DD {fmtPct(-a.maxDrawdownPct)} ·{' '}
              <span className={pnlClass(a.absoluteReturn)}>{fmtMoney(a.absoluteReturn)}</span> absolute after
              flows — DEMO.
            </p>
            <div className="hero-actions" style={{ justifyContent: 'center' }}>
              <Link to="/dashboard" className="btn btn-primary">
                Launch demo tracker
              </Link>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => modal.open(<EarlyAccessForm />, { title: 'Early access' })}
              >
                Early access
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
